/**
 * Engine tunables (engine.md §3). Seeded into `engine_config` by the migration runner and read
 * through `storage/config.ts` (typed getter). No number in engine code may duplicate these.
 *
 * Adding a key: add it here (and to engine.md §3). The runner inserts missing keys on every open
 * (`INSERT OR IGNORE`), so existing databases receive it without a schema migration.
 */

import type { PlanLimits } from '../contracts/billing.js';

/** The Free row of billing.md, as PlanLimits. Used when no entitlement is set. */
export const FREE_PLAN_LIMITS: PlanLimits = {
  sources: 3,
  messagesPerDay: 25,
  filters: 2,
  tags: 5,
  topics: 2,
  folders: 5,
  themes: 2,
  customTheme: false,
  aiScoring: false,
  syncDevices: 0,
  connectors: 0,
  connectorRequestsPerDay: 0,
  views: 50,
  // Data schema 3 (models.md §10): Free has the on-device model and needs its ladder.
  scoringRules: 10,
  activeModels: 1,
};

export const ENGINE_CONFIG_DEFAULTS = {
  'ingest.backfill_items': 30,
  /** First-fetch size given to a NEW source when the person does not pick one (sources.backfill_items). */
  'ingest.new_source_backfill_items': 10,
  'ingest.backfill_days': 14,
  'ingest.max_items_per_fetch': 100,
  'ingest.max_age_days': 180,
  'ingest.excerpt_chars': 320,
  'ingest.body_max_bytes': 262144,
  'ingest.title_max_chars': 300,
  /** Maximum remembered skipped external-id hashes per source cursor (engine.md §4 step 4). */
  'ingest.backfill_skip_max': 1000,
  'reading.words_per_minute': 220,
  'harvest.tick_seconds': 60,
  'harvest.concurrency': 4,
  'harvest.per_host_concurrency': 1,
  'harvest.timeout_ms': 20000,
  'harvest.min_pull_minutes': 5,
  'harvest.backoff_base_minutes': 5,
  'harvest.backoff_max_minutes': 1440,
  /** ± fraction applied to the next due time after a successful pull. */
  'harvest.jitter': 0.1,
  'pipeline.batch_size': 50,
  'pipeline.takeover_minutes': 10,
  'pipeline.cascade_max_depth': 8,
  'pipeline.dwell_sweep_minutes': 10,
  'pipeline.dwell_batch': 500,
  'content.retry_minutes': [5, 30, 120, 720] as number[],
  'scoring.batch_size': 20,
  /** The most body text Jev may be sent (the ceiling of the Jev record's options.bodyMaxChars; models.md §9.6). */
  'scoring.body_max_chars': 6000,
  /** Topic membership floor of a model record without the option (schema 2's single floor). */
  'scoring.topic_min_score': 15,
  'scoring.max_attempts': 5,
  'scoring.retry_base_minutes': 2,
  'scoring.upgrade_backfill_days': 7,
  /** Messages one rescore call (`messages.rescore`, `models.rescore`) may queue (models.md §5.5). */
  'scoring.rescore_max': 5000,
  /** Paraphrases per topic and their length (models.md §5.1; 16 so the owner's measured set — up to 13 a topic — fits). */
  'topics.max_phrases': 16,
  'topics.phrase_max_chars': 120,
  /** A topic's description (`topics.hint`), whoever reads it (models.md §5.0). */
  'topics.description_max_chars': 2000,
  /** Triggers per model run rule (models.md §7.1). */
  'models.max_triggers': 8,
  /** The on-device model's session is unloaded after this long without work (models.md §6.2). */
  'local_model.idle_unload_seconds': 120,
  /** Per tick, on-device scoring runs at most this long, in transactions of `slice_messages` (models.md §6.6). */
  'local_model.tick_budget_ms': 20000,
  'local_model.slice_messages': 16,
  /** Below this battery level (not charging) on-device scoring pauses (models.md §6.6). */
  'local_model.low_battery_level': 0.2,
  'retention.sweep_minutes': 60,
  'retention.batch': 500,
  'retention.tombstone_ttl_days': 400,
  'stats.freeze_days': 60,
  'stats.prior_kept': 1,
  'stats.prior_received': 5,
  'stats.usefulness_recompute_minutes': 60,
  'sync.push_debounce_ms': 3000,
  'sync.pull_interval_ms': 60000,
  'sync.batch_rows': 500,
  /** Mirrors server_config 'sync.max_batch_bytes': the device keeps each push under it. */
  'sync.max_batch_bytes': 2000000,
  'undo.max_entries': 20,
  'undo.ttl_ms': 600000,
  'fetch.hint_ttl_days': 7,
  /** A 429/503 (or Telegram FLOOD_WAIT) without a usable Retry-After waits this long (sources.md "Errors"). */
  'fetch.default_retry_after_minutes': 15,
  /** Per-kind floors for `sources.pull_minutes` above harvest.min_pull_minutes (Reddit: sources.md). */
  'harvest.min_pull_minutes_by_kind': { reddit: 15 } as Partial<Record<string, number>>,
  'reader.min_body_chars': 1500,
  'youtube.client': { clientName: 'ANDROID', clientVersion: '20.10.38', androidSdkVersion: 30 } as {
    clientName: string;
    clientVersion: string;
    androidSdkVersion: number;
  },
  /** Saved View / rule tree caps (engine.md §8.3). */
  'query.max_nodes': 100,
  'query.max_depth': 8,
  /** Entity name length (folders, tags, topics, rules, views): 1..this. */
  'entity.name_max_chars': 80,
  'plan.free': FREE_PLAN_LIMITS,
};

export type EngineConfig = typeof ENGINE_CONFIG_DEFAULTS;
export type EngineConfigKey = keyof EngineConfig;
export const ENGINE_CONFIG_KEYS = Object.keys(ENGINE_CONFIG_DEFAULTS) as EngineConfigKey[];
